import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Palette, Plus, X } from 'lucide-react';
import { problemDetail } from '@/shared/api/problem';
import {
  getGetApiV1ThemeActiveQueryKey,
  getGetApiV1ThemesQueryKey,
  useGetApiV1ThemeActive,
  useGetApiV1Themes,
  useGetApiV1ThemesId,
  usePostApiV1Themes,
  usePostApiV1ThemesIdActivate,
  usePutApiV1ThemesId,
} from '@/shared/api/generated/theme/theme';
import type { ThemeSummaryDto } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import { QueryError } from '@/shared/ui/QueryError';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { TableSkeleton } from '@/shared/ui/TableSkeleton';

/**
 * The palette, edited from the dashboard (§7).
 *
 * ⚠️ **§7's first sentence is "controlled from the admin panel", and until 10 September 2026 the
 * panel could not do it.** The endpoints existed — list, create, update, activate — and the only
 * screen over any of them was `Branding`, which writes the four image marks of whichever theme is
 * live. Changing a colour meant a database session, which is exactly what §7 exists to prevent.
 *
 * ⚠️ **And this is not decoration.** The mission-status and map colours are in the token set on
 * purpose: a control room reads that map under time pressure, and §7 keeps those semantics
 * adjustable without a release.
 *
 * ⚠️ **A theme is prepared and then published, never edited live.** Saving writes a draft nobody is
 * looking at; activating is what repaints every phone in the field. That separation is why
 * `GET /api/v1/themes/{id}` had to exist at all — see its own remarks.
 */
export function Themes() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error, refetch } = useGetApiV1Themes();

  // ⚠️ Read for one reason: a new theme starts as a COPY of the live palette. See [NewTheme].
  const { data: live } = useGetApiV1ThemeActive();

  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const themes = data?.items ?? [];

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetApiV1ThemesQueryKey() });

    // ⚠️ The live palette too: activating one is the whole point, and the panel repaints itself
    // from `theme/active` rather than from this list.
    void queryClient.invalidateQueries({ queryKey: getGetApiV1ThemeActiveQueryKey() });
  };

  const activate = usePostApiV1ThemesIdActivate({
    mutation: {
      onSuccess: () => {
        setFailure(null);
        invalidate();
      },
      onError: (problem) => setFailure(problemDetail(problem) ?? t.themes.saveFailed),
    },
  });

  const create = usePostApiV1Themes({
    mutation: {
      onSuccess: (id) => {
        setFailure(null);
        setAdding(false);
        setEditing(id as unknown as string);
        invalidate();
      },
      onError: (problem) => setFailure(problemDetail(problem) ?? t.themes.saveFailed),
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader title={t.themes.title} lede={t.themes.subtitle} />

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      {failure && (
        <p role="alert" className="rounded-lg bg-error-50 p-3 text-sm text-error-600">
          {failure}
        </p>
      )}

      <Panel
        title={t.themes.heading}
        actions={
          <Button onClick={() => setAdding((value) => !value)}>
            {adding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {adding ? t.themes.cancel : t.themes.add}
          </Button>
        }
        bodyClassName="flex flex-col"
      >
        {adding && (
          <NewTheme
            pending={create.isPending}
            // ⚠️ **A copy of the live palette, never an empty document.** A theme with no tokens
            // renders nothing, and the association's next palette is almost always a small change
            // to the one they have — starting blank would make every new theme a transcription
            // exercise with eighteen chances to mistype a colour.
            //
            // ⚠️ And it is disabled until the live palette has arrived, rather than falling back to
            // `{}`: a theme created from a failed read would look like a theme and be a blank
            // screen the moment somebody activated it.
            disabled={!live?.tokensJson}
            onSave={(name) =>
              create.mutate({ data: { name, tokensJson: live!.tokensJson! } })
            }
          />
        )}

        {isError ? null : isPending ? (
          <TableSkeleton rows={3} columns={4} />
        ) : themes.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.themes.name}</TableHead>
                <TableHead>{t.themes.version}</TableHead>
                <TableHead>{t.themes.state}</TableHead>
                <TableHead>{t.themes.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {themes.map((theme) => (
                <ThemeRow
                  key={theme.id}
                  theme={theme}
                  locale={locale}
                  pending={activate.isPending}
                  isEditing={editing === theme.id}
                  onEdit={() => setEditing((current) => (current === theme.id ? null : theme.id!))}
                  onActivate={() => activate.mutate({ id: theme.id! })}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState icon={Palette} title={t.themes.emptyTitle} hint={t.themes.empty} />
        )}
      </Panel>

      {editing && (
        <Editor
          themeId={editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
          onFailure={setFailure}
        />
      )}
    </div>
  );
}

function ThemeRow({
  theme,
  locale,
  pending,
  isEditing,
  onEdit,
  onActivate,
}: {
  theme: ThemeSummaryDto;
  locale: string;
  pending: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onActivate: () => void;
}) {
  const { t } = useLocale();

  return (
    <TableRow>
      <TableCell>{theme.name}</TableCell>
      <TableCell>{new Intl.NumberFormat(locale).format(theme.version ?? 0)}</TableCell>
      <TableCell>
        {theme.isActive ? (
          <Chip tone="approved">{t.themes.live}</Chip>
        ) : (
          <Chip tone="neutral">{t.themes.draft}</Chip>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onEdit}>
            {isEditing ? t.themes.close : t.themes.edit}
          </Button>

          {/* ⚠️ Absent on the live theme rather than disabled: there is nothing to activate. */}
          {!theme.isActive && (
            <Button variant="ghost" disabled={pending} onClick={onActivate}>
              {t.themes.activate}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function NewTheme({
  pending,
  disabled,
  onSave,
}: {
  pending: boolean;
  disabled: boolean;
  onSave: (name: string) => void;
}) {
  const { t } = useLocale();
  const [name, setName] = useState('');

  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-gray-200 p-4 dark:border-gray-800">
      <Field label={t.themes.name} htmlFor="theme-name">
        <input
          id="theme-name"
          aria-label={t.themes.name}
          className="w-56 rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <Button
        disabled={pending || disabled || name.trim().length === 0}
        onClick={() => onSave(name.trim())}
      >
        {t.themes.save}
      </Button>
    </div>
  );
}

/**
 * One theme's tokens, editable.
 *
 * ⚠️ **The document is parsed, mutated and written back — never rebuilt.** A token this build does
 * not know about survives the round trip, which is §7's whole model: a token can be added to the
 * document without every client learning about it first. Rebuilding the object from a fixed list of
 * fields would silently delete anything newer than this screen.
 */
function Editor({
  themeId,
  onClose,
  onSaved,
  onFailure,
}: {
  themeId: string;
  onClose: () => void;
  onSaved: () => void;
  onFailure: (message: string | null) => void;
}) {
  const { t } = useLocale();

  const { data, isPending, isError, error, refetch } = useGetApiV1ThemesId(themeId);

  const [tokens, setTokens] = useState<Record<string, unknown> | null>(null);

  /**
   * Which of the two palettes the colour grid below is editing.
   *
   * ⚠️ **Daylight first, because daylight is what a handset paints by default.** Until 10 September
   * 2026 this screen edited `colors` and nothing else — the NIGHT palette — while `colorsLight` was
   * carried through the round trip untouched and invisible. An administrator could change every
   * colour, save, watch the version rise, read the new value back from the API, and see **not one
   * pixel change** on a phone. Measured that day: a National Day palette was authored, activated,
   * and pulled onto a real handset, and the screen was identical to the byte — because the device
   * was in daylight mode and the colours it paints were never on this screen.
   */
  const [daylight, setDaylight] = useState(true);

  useEffect(() => {
    if (!data?.tokensJson) return;

    try {
      setTokens(JSON.parse(data.tokensJson) as Record<string, unknown>);
      onFailure(null);
    } catch {
      // ⚠️ A document that will not parse is not editable, and pretending otherwise would let a
      // save overwrite it with an empty one.
      setTokens(null);
      onFailure(t.themes.unreadable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.tokensJson]);

  const save = usePutApiV1ThemesId({
    mutation: {
      onSuccess: () => {
        onFailure(null);
        onSaved();
      },
      onError: (problem) => onFailure(problemDetail(problem) ?? t.themes.saveFailed),
    },
  });

  if (isError) return <QueryError error={error} onRetry={() => void refetch()} />;
  if (isPending || !tokens) return <TableSkeleton rows={6} columns={2} />;

  const night = (tokens.colors ?? {}) as Record<string, string>;
  const day = tokens.colorsLight as Record<string, string> | undefined;

  // ⚠️ A theme may legitimately carry no daylight palette: the app treats that as "this theme has
  // no daylight", and inventing one here would author half a palette in the association's name.
  // The switch is simply not offered, and what is edited is the only palette there is.
  const hasDaylight = day !== undefined;
  const editingDay = daylight && hasDaylight;
  const colours = editingDay ? day! : night;
  const radius = (tokens.radius ?? {}) as Record<string, number>;
  const scale = ((tokens.typography as Record<string, unknown> | undefined)?.scale ?? {}) as Record<
    string,
    number
  >;

  const setColour = (key: string, value: string) =>
    setTokens(
      editingDay
        ? { ...tokens, colorsLight: { ...day, [key]: value } }
        : { ...tokens, colors: { ...night, [key]: value } },
    );

  const setRadius = (key: string, value: number) =>
    setTokens({ ...tokens, radius: { ...radius, [key]: value } });

  const setScale = (key: string, value: number) =>
    setTokens({
      ...tokens,
      typography: { ...(tokens.typography as Record<string, unknown>), scale: { ...scale, [key]: value } },
    });

  return (
    <Panel
      title={`${t.themes.editing} — ${data.name ?? ''}`}
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t.themes.close}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => save.mutate({ id: themeId, data: { tokensJson: JSON.stringify(tokens) } })}
          >
            {t.themes.save}
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        {data.isActive ? t.themes.editingLive : t.themes.editingDraft}
      </p>

      {hasDaylight ? (
        <div className="mb-4 flex gap-2" role="group" aria-label={t.themes.paletteGroup}>
          <Button
            variant={daylight ? 'primary' : 'ghost'}
            onClick={() => setDaylight(true)}
          >
            {t.themes.paletteDay}
          </Button>
          <Button
            variant={daylight ? 'ghost' : 'primary'}
            onClick={() => setDaylight(false)}
          >
            {t.themes.paletteNight}
          </Button>
        </div>
      ) : (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{t.themes.noDaylight}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(colours).map(([key, value]) => (
          <Colour
            key={`${editingDay ? 'day' : 'night'}:${key}`}
            token={key}
            label={t.themes.tokens[key as keyof typeof t.themes.tokens]}
            value={value}
            onChange={(next) => setColour(key, next)}
          />
        ))}
      </div>

      <h4 className="mt-6 mb-2 text-sm font-semibold">{t.themes.typography}</h4>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Object.entries(scale).map(([key, value]) => (
          <Number
            key={key}
            label={key}
            value={value}
            onChange={(next) => setScale(key, next)}
          />
        ))}
      </div>

      <h4 className="mt-6 mb-2 text-sm font-semibold">{t.themes.radius}</h4>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Object.entries(radius).map(([key, value]) => (
          <Number
            key={key}
            label={key}
            value={value}
            onChange={(next) => setRadius(key, next)}
          />
        ))}
      </div>
    </Panel>
  );
}

/**
 * One colour token.
 *
 * ⚠️ **The text field is the authority, not the swatch.** Some tokens carry eight hex digits —
 * `coverageFill` is a translucent fill — and a native colour input has no alpha channel: bound to
 * it, saving would quietly flatten a translucent overlay into a solid one that hides the map under
 * it.
 */
function Colour({
  token,
  label,
  value,
  onChange,
}: {
  token: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-2 dark:border-gray-800">
      <input
        type="color"
        aria-label={token}
        className="h-8 w-8 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
        value={value.slice(0, 7)}
        onChange={(event) => onChange(value.length > 7 ? event.target.value + value.slice(7) : event.target.value)}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{label ?? token}</span>
        <span className="block truncate font-mono text-xs text-gray-500 dark:text-gray-400">{token}</span>
      </span>
      <input
        aria-label={`${token} value`}
        className="w-24 rounded border border-gray-300 bg-transparent px-2 py-1 font-mono text-xs dark:border-gray-700"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Number({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-800">
      <span className="flex-1 font-mono text-xs">{label}</span>
      <input
        type="number"
        aria-label={label}
        className="w-20 rounded border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-700"
        value={value}
        onChange={(event) => onChange(globalThis.Number(event.target.value))}
      />
    </label>
  );
}
